import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ProgressRadar } from '../components/Radar';
import { ShareWheel } from '../components/ShareWheel';
import { Progress } from '../components/ui/progress';
import { Lock, Sparkles, Star, Feather, Target, Globe, NotebookPen } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function Synthesis() {
  const [data, setData] = useState(null);
  const [engagement, setEngagement] = useState('');
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    api.get('/synthesis').then((r) => {
      setData(r.data);
      setEngagement(r.data.engagement || '');
    }).catch(() => setData({ available: false }));
  }, []);

  const saveEngagement = async () => {
    try {
      await api.post('/synthesis/engagement', { text: engagement });
      toast.success(t('synthesis.engagementSaved'));
    } catch { toast.error(t('common.error')); }
  };

  if (!data) return <p className="text-brume py-20" data-testid="synthesis-loading">{t('common.loading')}</p>;

  if (!data.available) {
    const done = data.chapters_done ?? 0;
    return (
      <div className="max-w-xl mx-auto text-center py-20" data-testid="synthesis-locked">
        <span className="w-16 h-16 rounded-full bg-or-400/15 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-or-400" strokeWidth={2.5} />
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('synthesis.lockedTitle')}</h1>
        <p className="text-base sm:text-lg text-brume mt-4 leading-relaxed">
          {t('synthesis.lockedText')}{' '}
          {t('synthesis.youCompleted')} {done} {done > 1 ? t('common.chapters') : t('common.chapter')} {t('synthesis.outOf', { total: data.chapters_total ?? 28 })}
        </p>
        <Button onClick={() => navigate('/tableau-de-bord')} data-testid="synthesis-back-button"
          className="mt-8 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8">
          {t('synthesis.continue')}
        </Button>
      </div>
    );
  }

  const s = data.summary || {};
  const radarData = (data.radar || []).map((r) => ({ ...r, axis: t(`axes.${r.key}`, undefined, r.axis) }));

  return (
    <div className="max-w-3xl mx-auto" data-testid="synthesis-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}
        className="text-center">
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('synthesis.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-creme mt-3">
          {t('synthesis.title')}
        </h1>
        <p className="text-lg text-or-300 italic mt-4">{t('synthesis.subtitle')}</p>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
        {[
          { icon: Sparkles, label: t('synthesis.overall'), value: `${data.overall_percent}%`, testid: 'syn-overall' },
          { icon: Target, label: t('synthesis.missions'), value: s.missions_done ?? 0, testid: 'syn-missions' },
          { icon: Globe, label: 'Give Back', value: s.givebacks_done ?? 0, testid: 'syn-givebacks' },
          { icon: NotebookPen, label: t('youth.statCarnet'), value: s.carnet_notes ?? 0, testid: 'syn-carnet' },
        ].map((c, i) => (
          <motion.div key={c.testid} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: i * 0.05 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-5 text-center" data-testid={c.testid}>
            <c.icon className="w-6 h-6 text-or-400 mx-auto mb-2" strokeWidth={2.5} />
            <p className="font-fredoka font-semibold text-2xl text-creme">{c.value}</p>
            <p className="text-xs font-bold text-brume mt-1">{c.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8 mt-8">
        <h2 className="text-2xl font-semibold text-creme mb-2">{t('youth.myRadar')}</h2>
        <ProgressRadar data={radarData} testid="synthesis-radar" />
        <Progress value={data.overall_percent} className="h-3 rounded-full bg-nuit-700" />
        <ShareWheel data={radarData} percent={data.overall_percent} testid="share-wheel-synthesis" />
      </div>

      {data.phrases?.length > 0 && (
        <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8 mt-8" data-testid="synthesis-phrases">
          <h2 className="text-2xl font-semibold text-creme mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('synthesis.phrases')}
          </h2>
          <div className="space-y-3">
            {data.phrases.map((p, i) => (
              <blockquote key={i} className="border-l-4 border-or-400 pl-4 py-1">
                <p className="text-creme italic">{p.text}</p>
                <p className="text-xs text-brume mt-1">{t('common.chapterN', { n: p.chapter })}</p>
              </blockquote>
            ))}
          </div>
        </div>
      )}

      <div className="bg-or-400 rounded-3xl p-8 mt-8" data-testid="engagement-card">
        <h2 className="text-2xl font-semibold text-nuit-950 flex items-center gap-2 mb-4">
          <Feather className="w-6 h-6" strokeWidth={2.5} /> {t('synthesis.engagement')}
        </h2>
        <p className="text-nuit-800 font-semibold mb-3">{t('synthesis.engagementPrompt')}</p>
        <Textarea data-testid="engagement-input" rows={3} value={engagement}
          onChange={(e) => setEngagement(e.target.value)}
          placeholder={t('synthesis.engagementPlaceholder')}
          className="rounded-2xl bg-white/70 border-none text-nuit-950 placeholder:text-nuit-700/60 focus:ring-4 focus:ring-nuit-900/20" />
        <Button onClick={saveEngagement} data-testid="engagement-save-button" disabled={!engagement.trim()}
          className="mt-4 rounded-full bg-nuit-900 hover:bg-nuit-800 text-or-300 font-bold px-8">
          {t('synthesis.engagementSave')}
        </Button>
      </div>
    </div>
  );
}
